/**
 * Chain abstraction for the webapp UI.
 *
 * Build-time switch via VITE_CHAIN (preferred) or PUBLIC_CHAIN:
 *   "cardano" → Cardano POC (pocjacardano.petso.dev)
 *   anything else / unset → Solana (dev, staging, prod)
 *
 * Existing components should import `CHAIN_NAME`, `NETWORK_LABEL`,
 * `getExplorerTxUrl`, `getExplorerAssetUrl` from here instead of hard-coding
 * Solana strings / URLs.
 */
import { getSolanaClusterParam } from './solanaExplorer';
import {
  getCardanoTxUrl,
  getCardanoAssetUrl,
  getCardanoAddressUrl,
} from './cardanoExplorer';

// rsbuild source.define inlines `process.env.PUBLIC_CHAIN` → literal string
// at build time (see webapp/rsbuild.config.ts). A `typeof process` guard
// would prevent that inlining — the guard stays in the compiled bundle and
// at runtime `process` is undefined in the browser, so CHAIN fell back to
// "solana" even on the cardano-poc build. Reference process.env directly;
// the symbol never actually exists at runtime because it was replaced.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const process: any;
// Reference process.env.PUBLIC_CHAIN directly so rsbuild source.define can
// substitute the literal string at build time. Any optional chaining or
// `typeof process` guard around this expression blocks that substitution
// and leaves a runtime `process` reference that breaks in the browser.
const raw: string | undefined = process.env.PUBLIC_CHAIN;

export const CHAIN: 'solana' | 'cardano' =
  String(raw ?? 'solana').toLowerCase() === 'cardano' ? 'cardano' : 'solana';

export const CARDANO_NETWORK: string =
  process.env.PUBLIC_CARDANO_NETWORK || 'preview';

export const CHAIN_NAME = CHAIN === 'cardano' ? 'Cardano' : 'Solana';

export const NETWORK_LABEL =
  CHAIN === 'cardano'
    ? `Cardano ${CARDANO_NETWORK.charAt(0).toUpperCase() + CARDANO_NETWORK.slice(1)}`
    : (import.meta as any).env?.PUBLIC_APP_ENV === 'prod'
      ? 'Solana Mainnet'
      : 'Solana Devnet';

/** Explorer URL for a transaction (hash/signature). */
export function getExplorerTxUrl(id: string): string {
  if (CHAIN === 'cardano') return getCardanoTxUrl(id, CARDANO_NETWORK);
  return `https://explorer.solana.com/tx/${id}${getSolanaClusterParam()}`;
}

/** Explorer URL for an address (wallet/pubkey). */
export function getExplorerAddressUrl(id: string): string {
  if (CHAIN === 'cardano') return getCardanoAddressUrl(id, CARDANO_NETWORK);
  return `https://explorer.solana.com/address/${id}${getSolanaClusterParam()}`;
}

/** Explorer URL for an asset/mint/NFT. */
export function getExplorerAssetUrl(id: string): string {
  if (CHAIN === 'cardano') return getCardanoAssetUrl(id, CARDANO_NETWORK);
  return `https://explorer.solana.com/address/${id}${getSolanaClusterParam()}`;
}

/** Human-readable DID prefix display (`did:petso:...` vs `did:cardano:petso:...`). */
export function getDidPrefix(): string {
  return CHAIN === 'cardano' ? `did:cardano:petso:` : 'did:petso:';
}
